import type { EmpresaDetailed } from '@/types'

type CnpjLookupResult = Partial<EmpresaDetailed> & {
  nome?: string
  atividadePrincipal?: string
  telefone?: string
  email?: string
}

const emptyToUndefined = (value: unknown) => {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'string' && value.trim() === '') return undefined
  return value
}

/** Monta payload PATCH seguro a partir do formulário de edição da empresa. */
export function buildEmpresaUpdatePayload(editData: Partial<EmpresaDetailed>) {
  const cnpjDigits = editData.cnpj ? String(editData.cnpj).replace(/\D/g, '') : ''
  const cepDigits = editData.cep ? String(editData.cep).replace(/\D/g, '') : ''
  const telefone = editData.telefone ?? editData.phone

  const payload = {
    nome: emptyToUndefined(editData.nome ?? editData.nomeFantasia ?? editData.razaoSocial),
    razaoSocial: emptyToUndefined(editData.razaoSocial),
    nomeFantasia: emptyToUndefined(editData.nomeFantasia),
    cnpj: cnpjDigits.length > 0 ? cnpjDigits : undefined,
    contato: emptyToUndefined(editData.contato),
    cargo: emptyToUndefined(editData.cargo),
    telefone: emptyToUndefined(telefone),
    email: emptyToUndefined(editData.email),
    site: emptyToUndefined(editData.site ?? editData.website),
    segmento: emptyToUndefined(editData.segmento),
    plataforma: emptyToUndefined(editData.plataforma),
    modelo: emptyToUndefined(editData.modelo),
    faturamento: emptyToUndefined(editData.faturamento),
    trafego: emptyToUndefined(editData.trafego),
    endereco: emptyToUndefined(editData.endereco ?? editData.address),
    cep: cepDigits.length > 0 ? cepDigits : undefined,
    cidade: emptyToUndefined(editData.cidade),
    estado: editData.estado
      ? String(editData.estado).trim().toUpperCase().slice(0, 2)
      : undefined,
    situacao: emptyToUndefined(editData.situacao),
    naturezaJuridica: emptyToUndefined(editData.naturezaJuridica),
    atividadePrincipal: emptyToUndefined(editData.atividadePrincipal),
    responsavelAssinaturaNome: emptyToUndefined(editData.responsavelAssinaturaNome),
    responsavelAssinaturaEmail: emptyToUndefined(editData.responsavelAssinaturaEmail),
    responsavelFinanceiroNome: emptyToUndefined(editData.responsavelFinanceiroNome),
    responsavelFinanceiroEmail: emptyToUndefined(editData.responsavelFinanceiroEmail),
    parceiroId: editData.parceiroId || undefined,
  }

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  )
}

/** Monta payload PATCH da empresa a partir da consulta CNPJ (Brasil API ou cache local). */
export function buildEmpresaPatchFromCnpjLookup(
  empresa: CnpjLookupResult,
  rawCnpj: string,
  fallbackNome?: string,
) {
  const razaoSocial = empresa.razaoSocial?.trim()
  const nomeFantasia = (empresa.nomeFantasia ?? empresa.nome)?.trim()

  return {
    cnpj: (empresa.cnpj ?? rawCnpj).replace(/\D/g, ''),
    razaoSocial,
    nomeFantasia,
    nome: nomeFantasia || razaoSocial || fallbackNome,
    endereco: empresa.endereco?.trim() || undefined,
    cep: empresa.cep?.replace(/\D/g, '') || undefined,
    cidade: empresa.cidade?.trim() || undefined,
    estado: empresa.estado?.trim().toUpperCase().slice(0, 2) || undefined,
    segmento: empresa.segmento?.trim() || undefined,
    situacao: empresa.situacao?.trim() || undefined,
    naturezaJuridica: empresa.naturezaJuridica?.trim() || undefined,
    atividadePrincipal: empresa.atividadePrincipal?.trim() || undefined,
    telefone: empresa.telefone?.trim() || undefined,
    email: empresa.email?.trim() || undefined,
  }
}
